<template>
  <div class="members-import">
    <h3 class="members-import__title">{{ translateText('hubstr.resident.members_import.title') }}</h3>

    <!-- Шаг 1: выбор файла (раскладка — из настройки клуба members_import.schema) -->
    <template v-if="state.step === 'upload'">
      <p class="members-import__label">
        {{ translateText('hubstr.resident.members_import.schema_label') }}:
        <b>{{ translateText(`hubstr.resident.members_import.schemas.${state.schema}`) }}</b>
      </p>
      <p class="members-import__hint">
        {{ translateText('hubstr.resident.members_import.template_hint') }}
        <b>{{ MEMBERS_IMPORT_SCHEMAS[state.schema].join(' | ') }}</b>
      </p>
      <p class="members-import__template">
        <el-link
          type="primary"
          :disabled="state.loading || state.templateLoading"
          @click="downloadTemplate"
        >
          {{ translateText('hubstr.resident.members_import.download_template') }}
        </el-link>
      </p>

      <magner-form-dropzone
        v-model="state.file"
        :field="dropZoneField"
        @update:modelValue="fileSelected"
      />
      <p v-if="state.loading" class="members-import__hint">
        {{ translateText('hubstr.resident.members_import.validating') }}
      </p>
    </template>

    <!-- Шаг 2: итог проверки -->
    <template v-else-if="state.step === 'validated' && state.import">
      <p class="members-import__file">{{ state.import.fileName }}</p>
      <ul class="members-import__stats">
        <li>{{ translateText('hubstr.resident.members_import.total_rows') }}: <b>{{ state.import.totalRows }}</b></li>
        <li>{{ translateText('hubstr.resident.members_import.valid_count') }}: <b>{{ state.import.validCount }}</b></li>
        <li>
          {{ translateText('hubstr.resident.members_import.invalid_count') }}: <b>{{ state.import.invalidCount }}</b>
        </li>
      </ul>

      <el-alert
        :title="translateText('hubstr.resident.members_import.ttl_warning')"
        type="warning"
        :closable="false"
        show-icon
      />

      <el-table
        v-if="state.import.errors?.length"
        :data="state.import.errors"
        class="members-import__table"
        max-height="300"
      >
        <el-table-column :label="translateText('hubstr.resident.members_import.table.row')" prop="row" width="80" />
        <el-table-column :label="translateText('hubstr.resident.members_import.table.column')" width="140">
          <template #default="{ row }">{{ columnName(row.column) }}</template>
        </el-table-column>
        <el-table-column :label="translateText('hubstr.resident.members_import.table.reason')" prop="reason" />
      </el-table>

      <div class="members-import__actions">
        <el-button :disabled="state.loading" @click="reset">
          {{ translateText('hubstr.resident.members_import.reupload') }}
        </el-button>
        <el-button
          type="primary"
          :disabled="!state.import.canStart"
          :loading="state.loading"
          @click="startImport"
        >
          {{ translateText('hubstr.resident.members_import.start') }}
        </el-button>
      </div>
    </template>

    <!-- Шаг 3: выполнение и итог -->
    <template v-else-if="state.step === 'processing' && state.import">
      <p class="members-import__file">{{ state.import.fileName }}</p>
      <p>
        {{ translateText('hubstr.resident.members_import.status') }}:
        <b>{{ state.import.statusLabel || state.import.status }}</b>
      </p>

      <template v-if="isInProgress">
        <el-progress
          v-if="state.import.status === 'running'"
          :percentage="progress.percentage"
          :format="() => `${progress.processed} / ${state.import?.validCount}`"
        />
        <p v-else class="members-import__hint">{{ translateText('hubstr.resident.members_import.queued') }}</p>
      </template>

      <template v-else>
        <ul class="members-import__stats">
          <li>
            {{ translateText('hubstr.resident.members_import.created_count') }}:
            <b>{{ state.import.createdCount ?? 0 }}</b>
          </li>
          <li>
            {{ translateText('hubstr.resident.members_import.not_imported_count') }}:
            <b>{{ state.import.notImportedCount ?? 0 }}</b>
          </li>
        </ul>

        <el-alert
          v-if="state.import.status === 'failed'"
          :title="`${translateText('hubstr.resident.members_import.failed_error')}: ${state.import.error || ''}`"
          type="error"
          :closable="false"
          show-icon
        />

        <el-table
          v-if="state.import.notImported?.length"
          :data="state.import.notImported"
          class="members-import__table"
          max-height="300"
        >
          <el-table-column :label="translateText('hubstr.resident.members_import.table.row')" prop="row" width="80" />
          <el-table-column :label="translateText('hubstr.resident.members_import.table.reason')" prop="reason" />
        </el-table>
      </template>

      <div class="members-import__actions">
        <el-button v-if="!isInProgress" :disabled="state.loading" @click="reset">
          {{ translateText('hubstr.resident.members_import.reupload') }}
        </el-button>
        <el-button
          v-if="canContinue"
          type="primary"
          :loading="state.loading"
          @click="startImport"
        >
          {{ translateText('hubstr.resident.members_import.continue') }}
        </el-button>
      </div>
    </template>

    <p v-if="state.errorText" class="members-import__error">{{ state.errorText }}</p>
    <el-button
      v-if="state.expired"
      class="members-import__expired"
      @click="reset"
    >
      {{ translateText('hubstr.resident.members_import.reupload') }}
    </el-button>
  </div>
</template>

<script setup lang="ts">
import {
  computed, onBeforeUnmount, reactive,
} from 'vue';
import { ElProgress } from 'element-plus';
import { MagnerFormDropzone, translate, useTranslate } from 'magner';
import {
  membersImportGet, membersImportStart, membersImportTemplate, membersImportValidate,
} from 'features/residents/requests/members-import';
import {
  MEMBERS_IMPORT_POLL_INTERVAL,
  MEMBERS_IMPORT_SCHEMAS,
  MembersImport,
  MembersImportResult,
  MembersImportSchema,
} from 'features/residents/types/members-import';
import { getMembersImportSchema } from 'features/residents/utils/members-import-access';

type Step = 'upload' | 'validated' | 'processing';

const { customT } = useTranslate();
const translateText = (code: string) => customT(translate(code));

const state = reactive<{
  step: Step,
  schema: MembersImportSchema,
  file: File[] | null,
  loading: boolean,
  templateLoading: boolean,
  errorText: string,
  /** Импорт не найден (404): удалён через сутки без запуска — файл нужно проверить заново */
  expired: boolean,
  import: MembersImport | null,
}>({
  step: 'upload',
  schema: getMembersImportSchema(),
  file: null,
  loading: false,
  templateLoading: false,
  errorText: '',
  expired: false,
  import: null,
});

let pollTimer: ReturnType<typeof setTimeout> | null = null;
let destroyed = false;

const logInfo = (message: string, data?: unknown) => {
  if (import.meta.env.DEV) {
    console.info(`[MembersImport.dialog] ${message}`, data ?? '');
  }
};

logInfo('[FIX] club schema', { schema: state.schema });

const dropZoneField = {
  type: 'dropzone',
  name: 'file',
  label: translate('hubstr.resident.members_import.file_label'),
  props: {
    valueKey: 'id',
    srcKey: 'file',
    removable: true,
    formats: ['xlsx'],
    maxSize: 30,
    inputAtts: {
      accept: '.xlsx',
    },
  },
};

const isInProgress = computed(() => state.import?.status === 'queued' || state.import?.status === 'running');

const canContinue = computed(() => !isInProgress.value
  && (state.import?.status === 'failed' || state.import?.canStart === true));

/** Обработано строк: createdCount + (notImportedCount − invalidCount) из validCount */
const progress = computed(() => {
  const data = state.import;
  if (!data || !data.validCount) {
    return { processed: 0, percentage: 0 };
  }
  const processed = Math.max(
    0,
    (data.createdCount ?? 0) + ((data.notImportedCount ?? 0) - data.invalidCount),
  );
  return {
    processed,
    percentage: Math.min(100, Math.round((processed / data.validCount) * 100)),
  };
});

const columnName = (column: number): string => MEMBERS_IMPORT_SCHEMAS[state.import?.schema ?? state.schema][column - 1]
  ?? String(column);

const setStep = (step: Step) => {
  logInfo('step', { from: state.step, to: step });
  state.step = step;
};

const stopPolling = (reason: string) => {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
  logInfo('poll stop', { id: state.import?.id, status: state.import?.status, reason });
};

const handleError = (res: MembersImportResult) => {
  state.errorText = res.error ?? '';
  state.expired = res.status === 404;
};

const poll = async () => {
  if (destroyed || !state.import) {
    return;
  }

  const res = await membersImportGet(state.import.id);
  if (destroyed) {
    return;
  }

  if (res.error || !res.data) {
    console.warn('[MembersImport.dialog] poll stopped because of an error', { id: state.import.id, error: res.error });
    handleError(res);
    stopPolling('error');
    return;
  }

  state.import = res.data;
  logInfo('poll tick', {
    id: res.data.id,
    status: res.data.status,
    processed: progress.value.processed,
    validCount: res.data.validCount,
  });

  if (isInProgress.value) {
    pollTimer = setTimeout(poll, MEMBERS_IMPORT_POLL_INTERVAL);
  } else {
    stopPolling(res.data.status);
  }
};

const startPolling = () => {
  stopPolling('restart');
  logInfo('poll start', { id: state.import?.id });
  pollTimer = setTimeout(poll, MEMBERS_IMPORT_POLL_INTERVAL);
};

/**
 * Скачивает шаблон раскладки клуба. Content-Disposition бэк не открывает через CORS,
 * поэтому имя файла собираем сами в том же формате: members-import-<раскладка>.xlsx.
 */
const downloadTemplate = async () => {
  if (state.templateLoading) {
    return;
  }

  state.templateLoading = true;
  state.errorText = '';

  const res = await membersImportTemplate();
  if (destroyed) {
    return;
  }
  state.templateLoading = false;

  if (!res.blob) {
    console.warn('[FIX][MembersImport.dialog] template download failed', { status: res.status, error: res.error });
    state.errorText = res.error ?? '';
    return;
  }

  const url = URL.createObjectURL(res.blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `members-import-${state.schema}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);

  logInfo('[FIX] template downloaded', { schema: state.schema, size: res.blob.size });
};

const fileSelected = async () => {
  const file = state.file?.[0];
  if (state.loading || !file) {
    return;
  }

  state.loading = true;
  state.errorText = '';
  state.expired = false;

  const res = await membersImportValidate(file, state.schema);
  if (destroyed) {
    return;
  }

  if (res.error || !res.data) {
    handleError(res);
    state.file = null;
  } else {
    state.import = res.data;
    setStep('validated');
  }

  state.loading = false;
};

const startImport = async () => {
  if (state.loading || !state.import) {
    return;
  }

  state.loading = true;
  state.errorText = '';
  state.expired = false;

  const res = await membersImportStart(state.import.id);
  if (destroyed) {
    return;
  }
  state.loading = false;

  if (res.data) {
    state.import = res.data;
    setStep('processing');
    startPolling();
    return;
  }

  // 409: импорт уже запущен или завершён — просто подтягиваем актуальное состояние
  if (res.status === 409) {
    logInfo('start returned 409, switching to polling', { id: state.import.id });
    setStep('processing');
    await poll();
    return;
  }

  handleError(res);
};

const reset = () => {
  stopPolling('reset');
  state.file = null;
  state.import = null;
  state.errorText = '';
  state.expired = false;
  state.loading = false;
  setStep('upload');
};

onBeforeUnmount(() => {
  destroyed = true;
  stopPolling('unmount');
});
</script>

<style scoped>
.members-import__title {
  margin: 0 0 16px;
}
.members-import__label {
  margin: 0 0 8px;
}
.members-import__hint {
  margin: 8px 0 16px;
  color: #606266;
  word-break: normal;
}
.members-import__template {
  margin: 0 0 16px;
}
.members-import__file {
  font-weight: 600;
  margin: 0 0 8px;
  word-break: break-all;
}
.members-import__stats {
  margin: 0 0 16px;
  padding-left: 18px;
}
.members-import__table {
  margin-top: 16px;
}
.members-import__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}
.members-import__error {
  color: #ff3333;
  word-break: normal;
}
</style>
